import { z } from "zod";

import { keys } from "@/.generated/i18n/i18n-keys.generated";
import { TKiliDataTableDefinition } from "@/data/api/kili/common/datatable/data-table-definition";
import { kiliDataTableRequestResponse } from "@/data/api/kili/common/datatable/data-table-request.type";
import { KiliIdFilter, KiliStringFilter } from "@/data/api/kili/common/filters/kili-filter";
import KiliApi from "@/data/api/kili/kili-api";
import { SectionsMutationKeys } from "@/data/api/kili/sections/sections-query-keys";
import { TPatchSectionBody } from "@/data/api/kili/sections/sections.type";
import { SitesMutationKeys } from "@/data/api/kili/sites/sites-query-keys";

export const siteSectionEntitySchema = z.object({
  id: z.number(),
  name: z.string(),
  area: z.number().nullable(),
  modulatorCount: z.number(),
});

export type TSiteSection = z.infer<typeof siteSectionEntitySchema>;

interface IPatchSectionCommand extends TPatchSectionBody {
  id: number;
}

export const siteSectionsDataTable: TKiliDataTableDefinition<typeof siteSectionEntitySchema> = {
  schema: siteSectionEntitySchema,
  headers: {
    name: {
      label: keys.tools.siteManagement.siteSections.table.column.name,
      editable: {
        type: "string",
      },
    },
    area: {
      label: keys.tools.siteManagement.siteSections.table.column.surface,
      editable: {
        type: "number",
      },
    },
    modulatorCount: {
      label: keys.tools.siteManagement.siteSections.table.column.appliances,
      formatter: (value: number) => {
        return {
          translationPath: keys.global.count.appliances,
          opts: { count: value },
        };
      },
    },
  },
  filterableFields: {
    id: { filter: { filterType: KiliIdFilter }, key: "id" },
    name: {
      filter: { filterType: KiliStringFilter },
      key: "name",
      placeholder: keys.tools.siteManagement.siteSections.table.searchSection,
      icon: "pi pi-search",
    },
  },
  search: async (body) => {
    const siteId = body.params ? body.params["siteId"] : -1;

    return KiliApi.searchSiteSections(body, { params: { siteId } });
  },
  searchMutationKey(id: number) {
    return SitesMutationKeys.siteSections(id);
  },
  editRowOpts: {
    editFn: async (body: IPatchSectionCommand) => {
      return KiliApi.patchSiteSection(body, { params: { sectionId: body.id } });
    },

    editRowMutationKey() {
      return SectionsMutationKeys.patchSection();
    },

    editKeyId: "id",
  },
};

export const siteSectionsEntitiesResponse = kiliDataTableRequestResponse(siteSectionsDataTable.schema);
